import { useState } from "react";
import api from "../lib/api";
import {
  Building2,
  Users,
  TrendingUp,
  Wallet,
  GraduationCap,
  Store,
  MapPin,
  Sparkles,
  ShieldCheck,
  ArrowRight,
  BarChart3,
} from "lucide-react";

// Investor Demo Dashboard — premium dark theme, big numbers,
// password gated. Pass: hostro@investor2025 (configurable via INVESTOR_PASS env).
const fmtN = (n) => new Intl.NumberFormat("en-IN").format(n || 0);
const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

function KPI({ icon: Icon, label, value, sub, accent = "#34C97B" }) {
  return (
    <div
      className="rounded-2xl p-5 transition hover:scale-[1.01]"
      style={{
        background: "rgba(255,255,255,0.04)",
        backdropFilter: "blur(14px)",
        border: "1px solid rgba(52,201,123,0.25)",
      }}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/60">
        <Icon size={14} style={{ color: accent }} />
        {label}
      </div>
      <div className="text-3xl md:text-4xl font-black text-white mt-3" style={{ letterSpacing: "-0.02em" }}>
        {value}
      </div>
      {sub && <div className="text-xs text-white/50 mt-1">{sub}</div>}
    </div>
  );
}

function GrowthChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.bookings));
  return (
    <div
      className="rounded-2xl p-6"
      style={{
        background: "rgba(255,255,255,0.04)",
        backdropFilter: "blur(14px)",
        border: "1px solid rgba(52,201,123,0.25)",
      }}
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-wider text-white/60">Growth</div>
          <div className="text-lg font-bold text-white mt-0.5">Bookings — Last 6 Months</div>
        </div>
        <BarChart3 size={20} className="text-[#34C97B]" />
      </div>
      <div className="flex items-end gap-3 h-44">
        {data.map((d) => (
          <div key={d.month} className="flex-1 flex flex-col items-center justify-end">
            <div className="text-xs text-white/70 mb-1">{d.bookings}</div>
            <div
              className="w-full rounded-t-md transition-all"
              style={{
                height: `${(d.bookings / max) * 100}%`,
                minHeight: 4,
                background: "linear-gradient(180deg, #34C97B 0%, #22B36A 100%)",
                boxShadow: "0 0 18px rgba(52,201,123,0.35)",
              }}
            />
            <div className="text-[10px] text-white/50 mt-2">{d.month}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RevenueStreams({ streams }) {
  const total = streams.reduce((s, r) => s + (r.amount || 0), 0);
  return (
    <div
      className="rounded-2xl p-6"
      style={{
        background: "rgba(255,255,255,0.04)",
        backdropFilter: "blur(14px)",
        border: "1px solid rgba(52,201,123,0.25)",
      }}
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-wider text-white/60">Revenue</div>
          <div className="text-lg font-bold text-white mt-0.5">Streams Breakdown</div>
        </div>
        <div className="text-sm font-bold text-[#34C97B]">{fmtINR(total)}</div>
      </div>
      <div className="space-y-3">
        {streams.map((r) => (
          <div key={r.name}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/80">{r.name}</span>
              <span className="text-white font-semibold">{fmtINR(r.amount)}</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-white/10 mt-1.5 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: total ? `${(r.amount / total) * 100}%` : "0%",
                  background: "linear-gradient(90deg, #34C97B, #22B36A)",
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CityPresence({ cities }) {
  return (
    <div
      className="rounded-2xl p-6"
      style={{
        background: "rgba(255,255,255,0.04)",
        backdropFilter: "blur(14px)",
        border: "1px solid rgba(52,201,123,0.25)",
      }}
    >
      <div className="flex items-center gap-2 mb-4">
        <MapPin size={16} className="text-[#34C97B]" />
        <div className="text-xs uppercase tracking-wider text-white/60">Geographic Presence</div>
      </div>
      <div className="flex flex-wrap gap-2">
        {cities.length === 0 && <span className="text-sm text-white/50">No cities yet</span>}
        {cities.map((c) => {
          const isJaipur = (c || "").toLowerCase().includes("jaipur");
          return (
            <span
              key={c}
              className="px-3 py-1.5 rounded-full text-xs font-semibold border"
              style={{
                background: isJaipur ? "#34C97B" : "rgba(255,255,255,0.06)",
                color: isJaipur ? "#0D1B2A" : "rgba(255,255,255,0.85)",
                borderColor: isJaipur ? "#34C97B" : "rgba(255,255,255,0.15)",
                boxShadow: isJaipur ? "0 0 18px rgba(52,201,123,0.45)" : "none",
              }}
            >
              {c}
              {isJaipur && " · HQ"}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function PasswordGate({ onUnlock }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/metrics/investor", { password: pw });
      onUnlock(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Invalid pass");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background:
          "radial-gradient(1200px 700px at 15% -10%, rgba(52,201,123,0.18), transparent 60%), radial-gradient(900px 600px at 110% 110%, rgba(52,201,123,0.10), transparent 65%), #0D1B2A",
        fontFamily: "Inter, ui-sans-serif, system-ui",
      }}
      data-testid="investor-gate"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center gap-2">
            <svg width="40" height="40" viewBox="0 0 48 48">
              <rect x="2" y="2" width="44" height="44" rx="11" fill="#34C97B" />
              <path d="M11 24 L24 12 L37 24 L37 36 Q37 38 35 38 L13 38 Q11 38 11 36 Z" fill="#FFFFFF" />
              <rect x="20" y="26" width="8" height="12" rx="1.5" fill="#34C97B" />
            </svg>
            <span className="text-2xl font-black text-white">HOSTRO</span>
          </div>
          <p className="text-xs text-white/50 mt-2 uppercase tracking-widest">Investor Metrics · Restricted</p>
        </div>
        <form
          onSubmit={submit}
          className="rounded-2xl p-6"
          style={{
            background: "rgba(255,255,255,0.05)",
            backdropFilter: "blur(18px)",
            border: "1px solid rgba(52,201,123,0.35)",
          }}
        >
          <label className="text-xs font-semibold uppercase tracking-wider text-white/60">Investor Pass</label>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="w-full mt-1.5 px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white focus:border-[#34C97B] focus:ring-2 focus:ring-[#34C97B]/30 outline-none"
            placeholder="Enter pass"
            required
            data-testid="investor-pass-input"
          />
          {err && (
            <div className="mt-3 bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-xl p-3 text-sm">
              {err}
            </div>
          )}
          <button
            disabled={busy}
            className="w-full mt-4 inline-flex items-center justify-center gap-2 bg-[#34C97B] hover:bg-[#22B36A] disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition"
            data-testid="investor-pass-submit"
          >
            {busy ? "Verifying..." : (<>Unlock Metrics <ArrowRight size={16} /></>)}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function InvestorMetrics() {
  const [data, setData] = useState(null);

  if (!data) return <PasswordGate onUnlock={setData} />;

  return (
    <div
      className="min-h-screen py-8 px-4 md:px-10"
      style={{
        background:
          "radial-gradient(1200px 700px at 15% -10%, rgba(52,201,123,0.12), transparent 60%), radial-gradient(900px 600px at 110% 110%, rgba(52,201,123,0.08), transparent 65%), #0D1B2A",
        fontFamily: "Inter, ui-sans-serif, system-ui",
      }}
      data-testid="investor-dashboard"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <svg width="36" height="36" viewBox="0 0 48 48">
              <rect x="2" y="2" width="44" height="44" rx="11" fill="#34C97B" />
              <path d="M11 24 L24 12 L37 24 L37 36 Q37 38 35 38 L13 38 Q11 38 11 36 Z" fill="#FFFFFF" />
              <rect x="20" y="26" width="8" height="12" rx="1.5" fill="#34C97B" />
            </svg>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50">Hostro · Investor View</div>
              <div className="text-xl md:text-2xl font-black text-white" style={{ letterSpacing: "-0.02em" }}>
                Platform Metrics — Real Time
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#34C97B]/40 text-[#34C97B] bg-[#34C97B]/10">
            <ShieldCheck size={14} /> Confidential
          </div>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI icon={Building2} label="Total PGs" value={fmtN(data.total_pgs)} sub={`${data.active_pgs} active`} />
          <KPI icon={Users} label="Active Tenants" value={fmtN(data.total_students)} sub="Residents on platform" />
          <KPI icon={Store} label="Owners / Operators" value={fmtN(data.total_owners)} sub="PMS subscribers" />
          <KPI icon={Wallet} label="Platform GMV" value={fmtINR(data.gmv)} sub="Lifetime rent collected" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <KPI icon={TrendingUp} label="Monthly Bookings" value={fmtN(data.monthly_bookings)} sub="Current month" />
          <KPI icon={Sparkles} label="Occupancy" value={`${data.occupancy_pct}%`} sub={`${data.beds_occupied}/${data.beds_total} beds`} />
          <KPI icon={GraduationCap} label="Ambassadors" value={fmtN(data.ambassadors_total)} sub={`${data.ambassador_cities.length} cities`} />
          <KPI icon={Store} label="Vendor Partners" value={fmtN(data.total_vendors)} sub="Food + laundry + more" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
          <div className="lg:col-span-2"><GrowthChart data={data.growth} /></div>
          <RevenueStreams streams={data.revenue_streams} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <KPI icon={Wallet} label="MRR Tracker" value={fmtINR(data.mrr)} sub="Monthly recurring run-rate" />
          <KPI icon={Wallet} label="Food GMV" value={fmtINR(data.food_gmv)} sub="Delivery volume" />
          <KPI icon={Wallet} label="Laundry GMV" value={fmtINR(data.laundry_gmv)} sub="Order volume" />
        </div>

        <div className="mt-6">
          <CityPresence cities={data.cities} />
        </div>

        <div className="text-center text-xs text-white/40 mt-10">
          © {new Date().getFullYear()} Hostro Ventures Pvt Ltd · Where You Belong
        </div>
      </div>
    </div>
  );
}
